import React from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { FaInstagram, FaSignOutAlt } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";

const goldShine = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const FooterContainer = styled.footer`
  width: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0),
    var(--background-secondary)
  );
  padding: 2rem 0;
  margin-top: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(212, 175, 55, 0) 0%,
      rgba(212, 175, 55, 0.3) 50%,
      rgba(212, 175, 55, 0) 100%
    );
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const FooterLeft = styled.div`
  flex: 1;
`;

const FooterRight = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const FooterText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Copyright = styled.p`
  color: var(--text-tertiary);
  font-size: 0.8rem;
`;

const LogoutButton = styled.button`
  color: var(--text-secondary);
  font-size: 0.9rem;
  border: 1px solid var(--border-color);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  transition: var(--transition);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: var(--primary-color);
    border-color: var(--primary-color);
    background-color: rgba(212, 175, 55, 0.05);
  }

  svg {
    font-size: 0.9rem;
  }
`;

const InstagramLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--background-tertiary);
  transition: transform 0.3s;
  border: 1px solid rgba(212, 175, 55, 0.2);

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(212, 175, 55, 0.4);
  }

  &:after {
    display: none;
  }

  svg {
    font-size: 1.3rem;
    background: var(--gold-gradient);
    background-size: 200% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 0 2px rgba(212, 175, 55, 0.3));
    transition: filter 0.3s;
    animation: ${goldShine} 4s linear infinite;
  }

  &:hover svg {
    filter: drop-shadow(0 0 4px rgba(212, 175, 55, 0.5));
  }
`;

const Footer = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterLeft>
          <FooterText>AutoGoldmine - Premium Script Generation</FooterText>
          <Copyright>
            &copy; {new Date().getFullYear()} AutoGoldMine. All rights reserved.
          </Copyright>
        </FooterLeft>

        <FooterRight>
          <InstagramLink
            href="https://www.instagram.com/autogoldmine"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaInstagram />
          </InstagramLink>

          {currentUser && (
            <LogoutButton onClick={handleLogout}>
              <FaSignOutAlt /> Logout
            </LogoutButton>
          )}
        </FooterRight>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
